/**
 * Produccion — pestaña de ensamblaje: la declaración del cliente se convierte en Anexo.
 *
 * Decisiones de diseño:
 * - Split OBLIGATORIO 40/60 (principio 13): hilo de WhatsApp a la izquierda
 *   (grid-cols-[2fr_3fr] en desktop), encomienda de trabajo a la derecha. El
 *   operador no tiene la caja: el hilo es la verdad y nunca sale de pantalla.
 *   Bajo lg se apila (hilo primero) — la superficie es desktop-first a 1440px.
 * - Autosave SILENCIOSO cada 3 s tras la última edición (principio 8): sin botón
 *   de guardado obligatorio; un indicador pasivo ('Guardando…' / 'Guardado hace
 *   un momento') y un 'Guardar borrador' de pura reassurance.
 * - Estado transversal en un solo dueño: quote buffer (cita del cliente),
 *   jump-to-message (procedencia) y preguntas al cliente.
 * - MODALES SOLO para acciones terminales (principio 7): Generar Anexo (con
 *   vista previa en el orden BLOQUEADO del Anexo y advertencia de finalidad,
 *   Ctrl+G) y Descartar (irreversible). Pedir aclaración usa popover.
 * - Tras 'Pedir aclaración' a nivel encomienda la tarjeta vuelve al Manifiesto
 *   (onBack) con estado esperando_cliente — la pelota queda del lado del cliente.
 *   Las preguntas por campo NO expulsan al operador: siguen editando.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, FileCheck2, Trash2, RotateCw } from 'lucide-react';
import {
  getDraft,
  updateDraft,
  sendClientQuestion,
  generateAnexo,
  discardDraft,
} from '../../api/mock/recepcionDigital';
import WhatsAppThreadPanel from './WhatsAppThreadPanel';
import WorkingEncomienda, { ordenarPaquetesAnexo } from './WorkingEncomienda';
import LiveTotalsBar from './LiveTotalsBar';
import AskClientButton from './shared/AskClientButton';

// ── Modal terminal (Generar Anexo / Descartar) ────────────────────────────────
const ModalTerminal = ({ titulo, onCerrar, children }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCerrar]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 18, 22, 0.55)' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCerrar();
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl border p-5"
        style={{ background: 'var(--tile)', borderColor: 'var(--border)', boxShadow: 'var(--tile-shadow)' }}
      >
        <h2 className="mb-3 text-base font-bold" style={{ color: 'var(--text-main)' }}>
          {titulo}
        </h2>
        {children}
      </div>
    </div>
  );
};

// ── Vista previa compacta del Anexo (orden bloqueado) ─────────────────────────
const VistaPreviaAnexo = ({ packages }) => {
  const ordenados = ordenarPaquetesAnexo(packages);
  let granTotal = 0;

  return (
    <div
      className="overflow-x-auto rounded-lg border font-mono text-[11px]"
      style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}
    >
      <div
        className="grid grid-cols-[28px_minmax(0,1fr)_40px_48px_48px_64px_64px] gap-1 border-b px-2 py-1.5 font-semibold"
        style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
        aria-hidden="true"
      >
        <span>No.</span>
        <span>Descripción</span>
        <span className="text-center">Cant.</span>
        <span className="text-center">Usado</span>
        <span className="text-center">Nuevo</span>
        <span className="text-right">Valor unit.</span>
        <span className="text-right">Total</span>
      </div>
      {ordenados.map((pkg, i) => {
        const items = pkg.items || [];
        const sub = items.reduce(
          (s, it) => s + (parseFloat(it.unit_value) || 0) * (parseInt(it.quantity) || 1),
          0
        );
        granTotal += sub;
        return (
          <div key={pkg.unique_package_id || i} className="border-b" style={{ borderColor: 'var(--border)' }}>
            <p className="px-2 pt-1.5 text-[11px] font-bold" style={{ color: 'var(--text-main)' }}>
              {i + 1}. {(pkg.recipient_name || 'SIN NOMBRE').toUpperCase()} — {pkg.unique_package_id}
            </p>
            {items.map((it) => {
              const qty = parseInt(it.quantity) || 1;
              const unit = parseFloat(it.unit_value) || 0;
              return (
                <div
                  key={it.id}
                  className="grid grid-cols-[28px_minmax(0,1fr)_40px_48px_48px_64px_64px] gap-1 px-2 py-0.5"
                  style={{ color: 'var(--text-main)' }}
                >
                  <span />
                  <span className="truncate">
                    {(it.description || '').toUpperCase().slice(0, 42)}
                  </span>
                  <span className="text-center">{qty}</span>
                  <span className="text-center">{(it.condition || 'NUEVO') !== 'NUEVO' ? 'X' : ''}</span>
                  <span className="text-center">{(it.condition || 'NUEVO') === 'NUEVO' ? 'X' : ''}</span>
                  <span className="text-right">{unit.toFixed(2)}</span>
                  <span className="text-right">{(qty * unit).toFixed(2)}</span>
                </div>
              );
            })}
            <p className="px-2 pb-1 text-right font-bold" style={{ color: 'var(--text-main)' }}>
              Subtotal: {sub.toFixed(2)}
            </p>
          </div>
        );
      })}
      <p className="px-2 py-1.5 text-right text-xs font-bold" style={{ color: 'var(--text-main)' }}>
        TOTAL USD$: {granTotal.toFixed(2)}
      </p>
    </div>
  );
};

const Produccion = ({ draftId, onBack }) => {
  const [draft, setDraft] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(null);

  const [quoteBuffer, setQuoteBuffer] = useState(null);
  const [jumpToMessageId, setJumpToMessageId] = useState(null);

  const [estadoGuardado, setEstadoGuardado] = useState('inactivo'); // inactivo | guardando | guardado | fallo
  const timerGuardadoRef = useRef(null);
  const draftRef = useRef(null);
  draftRef.current = draft;

  const [modalAnexo, setModalAnexo] = useState(false);
  const [modalDescartar, setModalDescartar] = useState(false);
  const [accionEnCurso, setAccionEnCurso] = useState(false);
  const [errorAccion, setErrorAccion] = useState(null);

  // ── Carga ──────────────────────────────────────────────────────────────────
  const cargar = useCallback(async () => {
    setCargando(true);
    setErrorCarga(null);
    try {
      const d = await getDraft(draftId);
      setDraft(d);
    } catch (e) {
      setErrorCarga(e.message || 'No se pudo abrir la encomienda.');
    } finally {
      setCargando(false);
    }
  }, [draftId]);

  useEffect(() => {
    cargar();
    return () => clearTimeout(timerGuardadoRef.current);
  }, [cargar]);

  // ── Autosave silencioso (3 s tras la última edición) ───────────────────────
  const programarAutosave = useCallback(() => {
    clearTimeout(timerGuardadoRef.current);
    timerGuardadoRef.current = setTimeout(async () => {
      const actual = draftRef.current;
      if (!actual) return;
      setEstadoGuardado('guardando');
      try {
        await updateDraft(draftId, { packages: actual.packages, status: actual.status });
        setEstadoGuardado('guardado');
      } catch (e) {
        setEstadoGuardado('fallo'); // reintenta con la próxima edición
      }
    }, 3000);
  }, [draftId]);

  const aplicarPaquetes = useCallback(
    (paquetesNuevos) => {
      setDraft((prev) => (prev ? { ...prev, packages: paquetesNuevos } : prev));
      programarAutosave();
    },
    [programarAutosave]
  );

  const guardarAhora = async () => {
    clearTimeout(timerGuardadoRef.current);
    const actual = draftRef.current;
    if (!actual) return;
    setEstadoGuardado('guardando');
    try {
      await updateDraft(draftId, { packages: actual.packages, status: actual.status });
      setEstadoGuardado('guardado');
    } catch (e) {
      setEstadoGuardado('fallo');
    }
  };

  // ── Preguntas al cliente ───────────────────────────────────────────────────
  // Por campo: la pregunta sale, el estado local pasa a esperando_cliente,
  // pero el operador SIGUE en Producción (solo la aclaración general expulsa).
  const preguntaPorCampo = useCallback(
    async (fieldRef, message) => {
      const d = await sendClientQuestion(draftId, { fieldRef, message });
      setDraft((prev) =>
        prev ? { ...prev, status: d.status, whatsapp_thread: d.whatsapp_thread } : prev
      );
    },
    [draftId]
  );

  const aclaracionGeneral = async (message) => {
    await sendClientQuestion(draftId, { fieldRef: 'general', message });
    onBack();
  };

  // ── Acciones terminales ────────────────────────────────────────────────────
  const confirmarGenerarAnexo = async () => {
    setAccionEnCurso(true);
    setErrorAccion(null);
    try {
      clearTimeout(timerGuardadoRef.current);
      const actual = draftRef.current;
      await updateDraft(draftId, { packages: actual.packages, status: actual.status });
      await generateAnexo(draftId);
      setModalAnexo(false);
      onBack();
    } catch (e) {
      setErrorAccion(e.message || 'No se pudo generar el Anexo.');
    } finally {
      setAccionEnCurso(false);
    }
  };

  const confirmarDescartar = async () => {
    setAccionEnCurso(true);
    setErrorAccion(null);
    try {
      await discardDraft(draftId);
      setModalDescartar(false);
      onBack();
    } catch (e) {
      setErrorAccion(e.message || 'No se pudo descartar la encomienda.');
    } finally {
      setAccionEnCurso(false);
    }
  };

  // ── Ctrl+G → modal Generar Anexo ───────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'g' || e.key === 'G')) {
        e.preventDefault();
        setModalAnexo(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Estados de carga ───────────────────────────────────────────────────────
  if (cargando) {
    return (
      <p className="px-2 py-10 text-center text-sm" style={{ color: 'var(--text-secondary)' }} role="status">
        Cargando encomienda…
      </p>
    );
  }
  if (errorCarga) {
    return (
      <div className="flex flex-col items-center gap-3 px-2 py-10 text-center">
        <p className="text-sm" style={{ color: 'var(--avianca-red)' }} role="alert">
          {errorCarga}
        </p>
        <button
          type="button"
          onClick={cargar}
          aria-label="Reintentar la carga de la encomienda"
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border px-4 text-sm font-medium
                     hover:bg-[var(--nav-active-bg)]
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
          style={{ borderColor: 'var(--border)', color: 'var(--text-main)' }}
        >
          <RotateCw size={14} aria-hidden="true" /> Reintentar
        </button>
      </div>
    );
  }
  if (!draft) return null;

  const textoGuardado = {
    inactivo: '',
    guardando: 'Guardando…',
    guardado: 'Guardado hace un momento',
    fallo: 'No se pudo guardar. Reintentando…',
  }[estadoGuardado];

  return (
    <div className="flex flex-col gap-3">
      {/* ── Barra superior: volver + cliente + guardado ── */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          aria-label="Volver al Manifiesto"
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-2 text-sm font-medium
                     hover:bg-[var(--nav-active-bg)]
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
          style={{ color: 'var(--text-secondary)' }}
        >
          <ArrowLeft size={15} aria-hidden="true" /> Manifiesto
        </button>
        <div className="min-w-0">
          <p className="truncate text-sm font-bold" style={{ color: 'var(--text-main)' }}>
            {draft.client?.name || 'Cliente'}
          </p>
          <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
            {draft.client?.phone} · {draft.client?.is_encomendero ? 'Encomendero' : 'Viajero'}
          </p>
        </div>
        <span
          className="ml-auto text-[11px]"
          style={{ color: estadoGuardado === 'fallo' ? 'var(--amber)' : 'var(--text-tertiary)' }}
          role="status"
          aria-live="polite"
        >
          {textoGuardado}
        </span>
        <button
          type="button"
          onClick={guardarAhora}
          aria-label="Guardar borrador ahora"
          className="rounded-md px-2 py-1 text-[11px] font-medium underline-offset-2 hover:underline
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
          style={{ color: 'var(--text-secondary)' }}
        >
          {estadoGuardado === 'guardado' ? '✓ Guardado' : 'Guardar borrador'}
        </button>
      </div>

      {/* ── Buffer de cita activo ── */}
      {quoteBuffer && (
        <div
          className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs"
          style={{ background: 'var(--blue-soft)', borderColor: 'var(--blue)', color: 'var(--text-main)' }}
          role="status"
        >
          <span className="min-w-0 flex-1 truncate">
            Fuente copiada: “{quoteBuffer.text}” — péguela en un artículo con «Pegar fuente».
          </span>
          <button
            type="button"
            onClick={() => setQuoteBuffer(null)}
            aria-label="Descartar la fuente copiada"
            className="shrink-0 font-medium underline-offset-2 hover:underline
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
            style={{ color: 'var(--blue-text)' }}
          >
            Descartar fuente
          </button>
        </div>
      )}

      {/* ── Split 40/60 ── */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[2fr_3fr]">
        <div className="lg:sticky lg:top-16 lg:h-[calc(100vh-170px)]">
          <WhatsAppThreadPanel
            thread={draft.whatsapp_thread}
            clientName={draft.client?.name}
            onQuote={setQuoteBuffer}
            jumpToMessageId={jumpToMessageId}
            onJumpHandled={() => setJumpToMessageId(null)}
          />
        </div>

        <div className="flex min-w-0 flex-col gap-3">
          <WorkingEncomienda
            draft={draft}
            onChangeDraft={aplicarPaquetes}
            quoteBuffer={quoteBuffer}
            onConsumeQuote={() => setQuoteBuffer(null)}
            onJumpToMessage={setJumpToMessageId}
            onAskClient={preguntaPorCampo}
          />

          {/* ── Pie pegajoso: totales + CTAs ── */}
          <div className="sticky bottom-0 z-10 overflow-hidden rounded-lg border" style={{ borderColor: 'var(--border)' }}>
            <LiveTotalsBar
              packages={draft.packages}
              customsConfig={draft.customs_config_snapshot}
            />
            <div
              className="flex flex-wrap items-center justify-end gap-2 border-t px-3 py-2"
              style={{ background: 'var(--tile)', borderColor: 'var(--border)' }}
            >
              <button
                type="button"
                onClick={() => setModalDescartar(true)}
                aria-label="Descartar esta encomienda"
                className="mr-auto inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-xs font-medium
                           hover:bg-[var(--red-soft)]
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--avianca-red)]"
                style={{ color: 'var(--avianca-red)' }}
              >
                <Trash2 size={13} aria-hidden="true" /> Descartar
              </button>

              <AskClientButton
                fieldLabel="la encomienda"
                defaultMessage={`Hola ${draft.client?.name?.split(' ')[0] || ''}, para completar su encomienda necesito una aclaración: `}
                onSend={aclaracionGeneral}
              />

              <button
                type="button"
                onClick={() => setModalAnexo(true)}
                aria-label="Generar Anexo (Ctrl+G)"
                title="Generar Anexo (Ctrl+G)"
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-5 text-sm font-semibold text-white
                           hover:opacity-90
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--avianca-red)] focus-visible:ring-offset-1
                           disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: 'var(--avianca-red)' }}
                disabled={(draft.packages || []).length === 0}
              >
                <FileCheck2 size={15} aria-hidden="true" /> Generar Anexo
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Modal: Generar Anexo ── */}
      {modalAnexo && (
        <ModalTerminal titulo="Generar Anexo" onCerrar={() => !accionEnCurso && setModalAnexo(false)}>
          <VistaPreviaAnexo packages={draft.packages} />
          <p className="mt-3 text-xs" style={{ color: 'var(--amber)' }}>
            Esta acción es definitiva: se generará el PDF y la encomienda saldrá de la cola.
          </p>
          {errorAccion && (
            <p className="mt-2 text-xs" style={{ color: 'var(--avianca-red)' }} role="alert">
              {errorAccion}
            </p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setModalAnexo(false)}
              disabled={accionEnCurso}
              aria-label="Cancelar la generación del Anexo"
              className="min-h-[44px] rounded-lg border px-4 text-sm font-medium
                         hover:bg-[var(--nav-active-bg)]
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]
                         disabled:opacity-40"
              style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={confirmarGenerarAnexo}
              disabled={accionEnCurso}
              aria-label="Confirmar y generar el Anexo"
              className="min-h-[44px] rounded-lg px-5 text-sm font-semibold text-white
                         hover:opacity-90
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--avianca-red)] focus-visible:ring-offset-1
                         disabled:opacity-40"
              style={{ background: 'var(--avianca-red)' }}
            >
              {accionEnCurso ? 'Generando…' : 'Confirmar y generar'}
            </button>
          </div>
        </ModalTerminal>
      )}

      {/* ── Modal: Descartar ── */}
      {modalDescartar && (
        <ModalTerminal titulo="Descartar encomienda" onCerrar={() => !accionEnCurso && setModalDescartar(false)}>
          <p className="text-sm" style={{ color: 'var(--text-main)' }}>
            Se descartará la encomienda de {draft.client?.name || 'este cliente'}.
          </p>
          <p className="mt-1 text-xs" style={{ color: 'var(--avianca-red)' }}>
            Esta acción no se puede deshacer.
          </p>
          {errorAccion && (
            <p className="mt-2 text-xs" style={{ color: 'var(--avianca-red)' }} role="alert">
              {errorAccion}
            </p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setModalDescartar(false)}
              disabled={accionEnCurso}
              aria-label="Cancelar el descarte"
              className="min-h-[44px] rounded-lg border px-4 text-sm font-medium
                         hover:bg-[var(--nav-active-bg)]
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]
                         disabled:opacity-40"
              style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={confirmarDescartar}
              disabled={accionEnCurso}
              aria-label="Confirmar el descarte de la encomienda"
              className="min-h-[44px] rounded-lg px-5 text-sm font-semibold text-white
                         hover:opacity-90
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--avianca-red)] focus-visible:ring-offset-1
                         disabled:opacity-40"
              style={{ background: 'var(--avianca-red)' }}
            >
              {accionEnCurso ? 'Descartando…' : 'Descartar definitivamente'}
            </button>
          </div>
        </ModalTerminal>
      )}
    </div>
  );
};

export default Produccion;

/*
Ejemplo de uso (desde el shell RecepcionDigital.jsx):
  <Produccion draftId={selectedDraftId} onBack={backToManifiesto} />
*/
